"use client";

import type React from "react";

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  step?: number;
  className?: string;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  step = 1,
  className = "",
}) => {
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (newValue <= value[1]) {
      onChange([newValue, value[1]]);
    }
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (newValue >= value[0]) {
      onChange([value[0], newValue]);
    }
  };

  // Calculate the position of the active track
  const activeTrackStyle = {
    left: `${((value[0] - min) / (max - min)) * 100}%`,
    right: `${100 - ((value[1] - min) / (max - min)) * 100}%`,
  };

  // Common styles for range inputs
  const commonInputStyles =
    "absolute top-0 left-0 w-full h-[20px] appearance-none bg-transparent pointer-events-auto cursor-pointer [-webkit-appearance:none] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:-webkit-appearance-none [&::-webkit-slider-thumb]:w-[15px] [&::-webkit-slider-thumb]:h-[15px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-black [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_2px_rgba(0,0,0,0.5)] [&::-moz-range-thumb]:w-[15px] [&::-moz-range-thumb]:h-[15px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-black [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-[0_0_2px_rgba(0,0,0,0.5)] [&::-webkit-slider-runnable-track]:w-full [&::-webkit-slider-runnable-track]:h-[1px] [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:border-none [&::-moz-range-track]:w-full [&::-moz-range-track]:h-[1px] [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:border-none";

  return (
    <div className={`relative h-8 flex items-center ${className}`}>
      {/* Track background */}
      <div className="absolute w-full h-1 bg-gray-300 rounded" />

      {/* Active track - only using inline styles for dynamic positioning */}
      <div className="absolute h-1 bg-black rounded" style={activeTrackStyle} />

      {/* Min thumb */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[0]}
        onChange={handleMinChange}
        className={`${commonInputStyles} z-10`}
      />

      {/* Max thumb */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value[1]}
        onChange={handleMaxChange}
        className={`${commonInputStyles} z-20`}
      />
    </div>
  );
};

export default RangeSlider;
